import React, { useState, useCallback, useContext, useEffect, useMemo } from 'react';
import { Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { AppContext } from './AppState';
import { getSelectedMats, useMaterials } from '../utils/materialsLib';
import { folderIsChild, folderMatches } from '../utils/formatLib';
import { applySelect } from '../utils/uiLib';
import { getComparator, getComparatorFolders, stableSort } from '../utils/tableLib';

import DeleteModal from './DeleteModal';
import GalleryItem from './GalleryItem';
import GalleryFolder from './GalleryFolder';
import ShareModal from './ShareModal';
import StitchModal from './StitchModal';
import StitchViewModal from './StitchViewModal';
import SelectionToolbar from './SelectionToolbar';


// Set to true to show selectable check-boxes in the list
const allowSelection = true;

const MaterialAction = Object.freeze({
  DELETE: 'delete',
  FAVOURITE: 'favourite',
  PUBLISH: 'publish',
  SELECT: 'select',
  TAGS: 'tags',
});

const useStyles = makeStyles((theme) => ({
  toolbar: {
    marginTop: theme.spacing(-5),
    marginBottom: theme.spacing(-3),
  },
  subtleText: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(3),
    color: theme.palette.secondary.light,
  },
}));

export default function MaterialGallery(props) {
  const {
    materials, folder, setFolder, folders, onMoveFolder,
    deleteMaterial, putFields, reloadMaterial,
    onSelectedTagsChanged, thumbnailUrls, order, setOrder, orderBy, setOrderBy, handleOrderByChange, sortKeys,
  } = props;

  const classes = useStyles();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [materialIdToAction, setMaterialIdToAction] = useState(null);
  const [modalMaterialId, setModalMaterialId] = useState(null);
  const [showStitchModal, setShowStitchModal] = useState(false);
  const [stitchViewModalMatProxy, setStitchViewModalMatProxy] = useState(null);
  const { selected, setSelected, lastSelected, setLastSelected } = useContext(AppContext);

  const sortedMaterials = useMemo(() => (
    materials ? stableSort(materials, getComparator(order, orderBy)) : []
  ), [materials, order, orderBy]);
  const filteredMaterials = sortedMaterials.filter(m => m.visible && folderMatches(m.folder, folder));
  const filteredFolders = folders.filter(f => materials.filter(m => m.folder === f && m.visible).length);
  const sortedFolders = (materials && folders) ? stableSort(
    filteredFolders.filter(f => materials.filter(m => m.folder === f && m.visible).length),
    getComparatorFolders(order, orderBy)
  ) : [];

  async function handleMoveFolder(target) {
    await onMoveFolder(target, getSelectedMats(materials, selected));
    setSelected([]);              // Clear selection
  }

  async function handlePublish(materialId, data) {
    await reloadMaterial(materialId, { publish: data });
  }
  function handleUnlock(materialId) {
    reloadMaterial(materialId, { unlocked: true });
  }

  // If a modal is open then get the latest copy of the material for the modal.
  const modalMaterial = (modalMaterialId && (showPublishModal || showDeleteModal)) ? (
    materials.find(m => m.materialId === modalMaterialId)
  ) : null;

  // We cascade through a proxy materialIdToAction state to avoid making GalleryItem depend on the full
  // material or the list of materials. This should possibly be rewritten as a reducer.
  const onClickDelete = useCallback((materialId) => {
    setMaterialIdToAction([materialId, MaterialAction.DELETE]);
  }, []);

  const onClickFavourite = useCallback((materialId) => {
    setMaterialIdToAction([materialId, MaterialAction.FAVOURITE]);
  }, []);

  const onClickPublish = useCallback((materialId) => {
    setMaterialIdToAction([materialId, MaterialAction.PUBLISH]);
  }, []);

  const onSelect = useCallback((materialId, event) => {
    setMaterialIdToAction([materialId, MaterialAction.SELECT, event]);
  }, []);

  const onTagsChanged = useCallback((materialId, tagsChanged) => {
    setMaterialIdToAction([materialId, MaterialAction.TAGS, tagsChanged]);
  }, []);

  useEffect(() => {
    async function actOnMaterial() {
      if (materialIdToAction) {
        const [materialId, action, payload] = materialIdToAction;
        const matAct = sortedMaterials.find(m => m.materialId === materialId);
        setMaterialIdToAction(null);
        if (matAct) {
          if (action === MaterialAction.DELETE) {
            setModalMaterialId(materialId);
            setShowDeleteModal(true);
          } else if (action === MaterialAction.FAVOURITE) {
            putFields({ [materialId]: { favourite: !matAct.favourite } });
          } else if (action === MaterialAction.PUBLISH) {
            setModalMaterialId(materialId);
            setShowPublishModal(true);
          } else if (action === MaterialAction.SELECT) {
              if (allowSelection) {
                const { newSelected, selectedMid } = applySelect(payload, materialId, sortedMaterials, selected, lastSelected, folder);
                setSelected(newSelected);
                setLastSelected(selectedMid);
              }
          } else if (action === MaterialAction.TAGS) {
            const { selected } = payload;
            putFields({ [materialId]: { tags: selected } });
          }
        }
      }
    };
    actOnMaterial();
  }, [folder, lastSelected, materialIdToAction, sortedMaterials, putFields, selected, setLastSelected, setSelected]);

  async function doOnDelete() {
    var res = false;
    if (modalMaterialId) {
      res = await deleteMaterial(modalMaterialId);
    }
    setShowDeleteModal(false);
    setMaterialIdToAction(null);
    return res;
  }

  return (<>
    <SelectionToolbar
      className={classes.toolbar}
      setShowStitchModal={setShowStitchModal}
      folder={folder}
      setFolder={setFolder}
      folders={folders}
      onMoveFolder={handleMoveFolder}
      onTagsChanged={onSelectedTagsChanged}
      inListView={false}
      materials={materials}
      order={order}
      setOrder={setOrder}
      orderBy={orderBy}
      setOrderBy={setOrderBy}
      handleOrderByChange={handleOrderByChange}
      sortKeys={sortKeys}
    />
    <Grid container>
      {sortedFolders.map((f) => (
        (folderIsChild(folder, f)) && (
          <GalleryFolder
            name={f}
            key={f}
            materials={sortedMaterials.filter(m => m.folder === f && m.visible)}
            thumbnailUrls={thumbnailUrls}
            setFolder={setFolder}
          />
        )
      ))}
      {filteredMaterials.map((material) => (
        <GalleryItem
          key={material.materialId}
          favourite={material.favourite}
          materialUserId={material.userId}
          materialId={material.materialId}
          name={material.name}
          publish={material.publish}
          qaStatus={material.qaStatus}
          status={material.status}
          stitched={material.stitched}
          success={material.success}
          tagsAuto={material['tags.auto']}
          tagsUser={material['tags.user']}
          texBakingStatus={material?.texBaking?.status}
          tiledUserPath={material.tiledUserPath}
          tilingIncluded={material.tilingIncluded}
          text={material.text}
          unlocked={material.unlocked}
          tilingAccepted={material.tilingAccepted}

          visible={material.visible}

          onClickDelete={onClickDelete}
          onClickFavourite={onClickFavourite}
          onClickPublish={onClickPublish}
          onTagsChanged={onTagsChanged}
          onSelect={onSelect}
          selected={selected.includes(material.materialId)}
          setStitchViewModalMatProxy={setStitchViewModalMatProxy}
          thumbnailUrl={thumbnailUrls[material.materialId]}
        />
      ))}
      {materials.length > 0 && filteredMaterials.length === 0 && filteredFolders.length === 0 && (
        <Typography className={classes.subtleText}>
          No materials matching your current filter
        </Typography>
      )}
    </Grid>
    {modalMaterialId && (
      <ShareModal
        material={modalMaterial}
        visible={showPublishModal}
        onHide={() => { setShowPublishModal(false); setModalMaterialId(null) }}
        unlock={modalMaterial?.unlocked}
        onUnlock={() => { handleUnlock(modalMaterialId); }}
        publish={modalMaterial?.publish}
        onPublish={(data) => { handlePublish(modalMaterialId, data) }}
      />
    )}
    {selected && selected.length >= 2 && (
      <StitchModal
        materials={getSelectedMats(materials, selected)}
        visible={showStitchModal}
        onHide={() => { setShowStitchModal(false); }}
      />
    )}
    {stitchViewModalMatProxy && (
      <StitchViewModal
        stitchedMaterialProxy={stitchViewModalMatProxy}
        visible={!!stitchViewModalMatProxy}
        onHide={() => { setStitchViewModalMatProxy(null); }}
      />
    )}
    <DeleteModal
      material={modalMaterial}
      visible={showDeleteModal}
      setVisible={setShowDeleteModal}
      onDelete={doOnDelete}
    />
  </>);
}
